import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroBackground from '../../components/Hero/HeroBackground';
import SiteNavigation from '../../components/Navigation/SiteNavigation';
import PageMeta from '../../components/SEO/PageMeta';
import './Home.css';
import './HomeMobile.css';
import './HomeLandscape.css';

const storySlides = [
  {
    eyebrow: 'Meet Atlas',
    title: 'All your files. One organized workspace.',
    body: 'Atlas brings files scattered across folders and drives into one centralized workspace, making them easier to find, organize, and use—without moving the originals.',
    actions: [{ to: '/atlas', label: 'Explore Atlas', primary: true }],
  },
  {
    eyebrow: 'One centralized view',
    title: 'Stop searching folder by folder and drive by drive.',
    body: 'Connect the locations you already use and work with related photos, documents, projects, and other files from one clear Atlas workspace.',
    actions: [{ to: '/atlas', label: 'See how Atlas works', primary: true }],
  },
  {
    eyebrow: 'Why Atlas',
    title: 'Organize what you already have.',
    points: [
      ['Across folders and drives', 'Bring scattered file locations into one workspace.'],
      ['Files stay in place', 'Atlas does not force you to move, rename, or duplicate originals.'],
      ['Find things faster', 'Search and browse your files from one centralized view.'],
      ['Built around your workflow', 'Organize information in a way that makes sense to you.'],
    ],
  },
  {
    eyebrow: 'Future Atlas ecosystem',
    title: 'Atlas comes first.',
    body: 'Future Atlas applications are planned to work with the same organized files and projects. Current development is focused on the main Atlas desktop experience.',
    actions: [{ to: '/atlas', label: 'Explore the main Atlas app', primary: true }],
  },
  {
    eyebrow: 'Your files remain yours',
    title: 'Organize locally. Keep control.',
    body: 'Atlas is designed to work with files stored on your own computer and drives, without turning your private files into advertising data.',
    actions: [{ to: '/privacy', label: 'Read the privacy principles', primary: true }],
  },
  {
    eyebrow: 'Pre-alpha testing',
    title: 'Help test Atlas with real files and real workflows.',
    body: 'We are looking for early testers who manage files across multiple folders and drives and can help improve the central Atlas experience.',
    actions: [{ to: '/pre-alpha', label: 'Apply for Pre-Alpha', primary: true }],
  },
];

export default function Home() {
  const [slideIndex, setSlideIndex] = useState(0);
  const wheelLocked = useRef(false);
  const touchStartX = useRef(null);
  const slide = slideIndex > 0 ? storySlides[slideIndex - 1] : null;
  const isLastSlide = slideIndex === storySlides.length;

  const showNextSlide = () => {
    setSlideIndex((currentIndex) => (
      currentIndex === storySlides.length ? 0 : currentIndex + 1
    ));
  };

  const showPreviousSlide = () => {
    setSlideIndex((currentIndex) => (
      currentIndex === 0 ? storySlides.length : currentIndex - 1
    ));
  };

  const handleWheel = (event) => {
    const horizontalMovement = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.shiftKey
        ? event.deltaY
        : 0;

    if (Math.abs(horizontalMovement) < 20 || wheelLocked.current) return;

    wheelLocked.current = true;
    horizontalMovement > 0 ? showNextSlide() : showPreviousSlide();
    window.setTimeout(() => { wheelLocked.current = false; }, 450);
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;

    const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const movement = touchStartX.current - touchEndX;
    touchStartX.current = null;

    if (Math.abs(movement) < 45) return;
    movement > 0 ? showNextSlide() : showPreviousSlide();
  };

  return (
    <>
      <PageMeta
        title="Atlas | Organize Files Across Folders and Drives"
        description="Atlas brings files scattered across folders and drives into one centralized desktop workspace, helping you find, organize, and use them without moving the originals."
        path="/"
      />
      <main className="home-page">
        <section
          className={`home-hero${slideIndex === 0 ? ' home-hero--cover' : ''}`}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <HeroBackground />
          <SiteNavigation />

          <div className={`home-story${slide ? ' is-showing-slide' : ' is-cover'}`}>
            {!slide && (
              <header className="home-cover-message">
                <h1>Find all your files in one organized place.</h1>
                <p>Photos, documents, and projects across your folders and drives—without moving the originals.</p>
              </header>
            )}

            {slide && (
              <article className="home-story__panel" key={slideIndex} aria-live="polite">
                <p className="home-story__eyebrow">{slide.eyebrow}</p>
                <h1>{slide.title}</h1>
                {slide.body && <p className="home-story__body">{slide.body}</p>}

                {slide.points && (
                  <div className="home-story__points">
                    {slide.points.map(([title, description]) => (
                      <div key={title}>
                        <h2>{title}</h2>
                        <p>{description}</p>
                      </div>
                    ))}
                  </div>
                )}

                {slide.actions && (
                  <div className="home-story__actions">
                    {slide.actions.map((action) => (
                      <Link
                        className={`button ${action.primary ? 'button--primary' : 'button--secondary'}`}
                        key={action.to}
                        to={action.to}
                      >
                        {action.label}
                      </Link>
                    ))}
                  </div>
                )}
              </article>
            )}

            <div className="home-story__controls">
              <span>{slideIndex === 0 ? 'Scroll right to learn more' : `${slideIndex} of ${storySlides.length}`}</span>

              {slideIndex > 0 && (
                <button
                  type="button"
                  className="home-story__arrow home-story__arrow--previous"
                  onClick={showPreviousSlide}
                  aria-label="Show previous section"
                >
                  <span aria-hidden="true">←</span>
                </button>
              )}

              <button
                type="button"
                className="home-story__arrow home-story__arrow--next"
                onClick={showNextSlide}
                aria-label={isLastSlide ? 'Return to opening image' : 'Show next section'}
              >
                <span aria-hidden="true">{isLastSlide ? '↻' : '→'}</span>
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
